import type {
  DrawCellCallback,
  DrawHeaderCallback,
  SizedGridColumn,
} from "@glideapps/glide-data-grid";
import { useCallback } from "react";
import type { IAiSpreadsheetRegion } from "@/payroll/ai-script/types";
import {
  type AdaptedSpreadsheetSheet,
  manualRowMarkerColor,
  overrideMarkerColor,
} from "./adapter";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";
import {
  drawCommentCornerMarker,
  drawCopiedRangeBorder,
  drawOverrideCornerMarker,
  drawSelectedHeaderBackground,
} from "./cell-markers";
import { drawColumnControlMarker } from "./column-control-marker";
import { drawFrozenColumnDivider } from "./column-freeze";
import {
  hasActiveSpreadsheetColumnFilter,
  type SpreadsheetRegionViewStates,
} from "./spreadsheet-region-view";
import { drawTextOverflowCell } from "./text-overflow";
import type { ViewerComments } from "./use-viewer-comments";
import type { useWorkbookClipboard } from "./use-workbook-clipboard";
import { findRegionHeaderForRecord } from "./viewer-helpers";

interface UseGridDrawCallbacksParams {
  activeRegionViewStates: SpreadsheetRegionViewStates;
  activeRegions: IAiSpreadsheetRegion[];
  activeSheetValue: string;
  adaptedSheet: AdaptedSpreadsheetSheet | null;
  columns: SizedGridColumn[];
  copiedSelection: ReturnType<typeof useWorkbookClipboard>["copiedSelection"];
  frozenColumn: number | null;
  isColumnInSelectedRange: (columnIndex: number) => boolean;
  comments: Pick<
    ViewerComments,
    "areCommentsEnabled" | "commentIndex" | "savedCellCoordinate"
  >;
}

export const useGridDrawCallbacks = ({
  activeRegionViewStates,
  activeRegions,
  activeSheetValue,
  adaptedSheet,
  columns,
  copiedSelection,
  frozenColumn,
  isColumnInSelectedRange,
  comments: { areCommentsEnabled, commentIndex, savedCellCoordinate },
}: UseGridDrawCallbacksParams) => {
  const drawCell = useCallback<DrawCellCallback>(
    (args, drawContent) => {
      drawTextOverflowCell({
        args,
        columns,
        drawContent,
        textOverflow: adaptedSheet?.getTextOverflow(args.col, args.row),
      });

      const record = adaptedSheet?.getCellRecord(args.col, args.row);
      if (record?.override) {
        drawOverrideCornerMarker(
          args.ctx,
          args.rect,
          overrideMarkerColor(record.override),
        );
      } else if (
        record?.manual_row_id &&
        record.computed_value != null &&
        String(record.computed_value) !== ""
      ) {
        drawOverrideCornerMarker(args.ctx, args.rect, manualRowMarkerColor());
      }
      if (areCommentsEnabled) {
        const saved = savedCellCoordinate(args.col, args.row, record);
        if (commentIndex.hasOpenThreadAt(saved.row, saved.col)) {
          drawCommentCornerMarker(args.ctx, args.rect);
        }
      }

      const headerRegion = record
        ? findRegionHeaderForRecord(activeRegions, record)
        : undefined;
      const headerSetting = headerRegion?.column_settings[String(record?.col)];
      if (
        record &&
        headerRegion &&
        (headerSetting?.filterable || headerSetting?.sortable)
      ) {
        const viewState = activeRegionViewStates[headerRegion.id];
        const filter = viewState?.filters[String(record.col)];
        const filtered = hasActiveSpreadsheetColumnFilter(filter);
        const sortDirection =
          viewState?.sort?.column === record.col
            ? viewState.sort.direction
            : null;

        drawColumnControlMarker(args.ctx, args.rect, {
          filtered,
          sortDirection,
          textColor: args.theme.textDark,
          backgroundColor: args.theme.bgCell,
          label: getAiSpreadsheetCellDisplayValue(record),
          font: `${args.theme.baseFontStyle} ${args.theme.fontFamily}`,
        });
      }

      if (copiedSelection?.sheetId === activeSheetValue) {
        drawCopiedRangeBorder(
          args.ctx,
          args.rect,
          { col: args.col, row: args.row },
          copiedSelection.range,
        );
      }

      drawFrozenColumnDivider(args.ctx, args.rect, args.col, frozenColumn);
    },
    [
      activeRegionViewStates,
      activeRegions,
      activeSheetValue,
      adaptedSheet,
      areCommentsEnabled,
      columns,
      commentIndex,
      copiedSelection,
      frozenColumn,
      savedCellCoordinate,
    ],
  );

  const drawGridHeader = useCallback<DrawHeaderCallback>(
    (args, drawContent) => {
      if (args.isSelected || isColumnInSelectedRange(args.columnIndex)) {
        drawSelectedHeaderBackground(args.ctx, args.rect, args.isSelected);
      }

      drawContent();

      drawFrozenColumnDivider(
        args.ctx,
        args.rect,
        args.columnIndex,
        frozenColumn,
      );
    },
    [frozenColumn, isColumnInSelectedRange],
  );

  return { drawCell, drawGridHeader };
};
